import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { SearchBar } from '@/components/SearchBar';
import { MissingPersonCard } from '@/components/MissingPersonCard';
import { CaseListItem } from '@/components/CaseListItem';
import { QuickFilterBar } from '@/components/QuickFilterBar';
import { LogoIcon } from '@/components/Logo';
import { SEO } from '@/components/SEO';
import { SearchResultsNudge, FooterNudge, useActionTracker, GentleUpgradeModal } from '@/components/UpgradeNudges';
import { getCases, getExternalCases, getBrowseCases, getExternalStats, getBrowseFacets, aiSearch, smartSearch } from '@/lib/api';
import { trackSearchPerformed } from '@/lib/telemetry';
import { toast } from 'sonner';
import { Filter, AlertTriangle, Clock, Check, Grid, List, Database, Users, Loader2, Scale, X, ChevronRight, Map, Sparkles, Info, ChevronDown, SlidersHorizontal, Search, Share2, MessageCircleWarning, CheckCircle2, Navigation, Camera, ArrowUpDown, ArrowLeft, Ruler, Weight, MapPin } from 'lucide-react';

export default function BrowseCases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [externalCases, setExternalCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [urgentOnly, setUrgentOnly] = useState(searchParams.get('urgent') === 'true');
  const [viewMode, setViewMode] = useState('grid');
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || 'all');
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || 'all');
  const [genderFilter, setGenderFilter] = useState(searchParams.get('gender') || 'all');
  const [ageFilter, setAgeFilter] = useState(searchParams.get('age') || 'all');
  const [raceFilter, setRaceFilter] = useState(searchParams.get('race') || 'all');
  const [hairColorFilter, setHairColorFilter] = useState(searchParams.get('hair') || 'all');
  const [eyeColorFilter, setEyeColorFilter] = useState(searchParams.get('eye') || 'all');
  const [heightFilter, setHeightFilter] = useState(searchParams.get('height') || 'all');
  const [weightFilter, setWeightFilter] = useState(searchParams.get('weight') || 'all');
  const [cityFilter, setCityFilter] = useState(searchParams.get('city') || '');
  const [hasPhotoFilter, setHasPhotoFilter] = useState(searchParams.get('photo') || 'all');
  const [yearMissingFilter, setYearMissingFilter] = useState(searchParams.get('year') || 'all');
  const [doeDiscrepancyOnly, setDoeDiscrepancyOnly] = useState(searchParams.get('doe_discrepancy') === 'true');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [externalStats, setExternalStats] = useState(null);
  const [facets, setFacets] = useState(null);
  const [filteredTotal, setFilteredTotal] = useState(null);
  const [showMobileInfo, setShowMobileInfo] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recent');
  const [userLocation, setUserLocation] = useState(null);
  // Search query state for regular text search (moved here for activeFilterCount)
  const [searchQuery, setSearchQuery] = useState('');
  const CASES_PER_PAGE = 48;  // Reduced from 100 for better performance
  // Ref to the results section so we can scroll to it after a search completes.
  // Without this, hitting the orange Search button gives no obvious feedback —
  // the results render below the fold and the user can't tell the page updated.
  const resultsRef = useRef(null);
  
  // Smart default: Try to get user location for "nearest" sort option
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {
          // Location denied or unavailable - that's fine, we'll use "recent" sort
        },
        { timeout: 5000, maximumAge: 600000 } // 10 min cache
      );
    }
  }, []);
  
  // First-time AI search hint - show once only
  const [showAIHint, setShowAIHint] = useState(() => {
    return localStorage.getItem('aiSearchHintDismissed') !== 'true';
  });
  
  const dismissAIHint = () => {
    setShowAIHint(false);
    localStorage.setItem('aiSearchHintDismissed', 'true');
  };
  
  // "Why This Matters" message - show on first visit, auto-collapse after scroll
  const [showWhyMatters, setShowWhyMatters] = useState(() => {
    return localStorage.getItem('browseWhyMattersDismissed') !== 'true';
  });
  
  const dismissWhyMatters = () => {
    setShowWhyMatters(false);
    localStorage.setItem('browseWhyMattersDismissed', 'true');
  };
  
  // Auto-dismiss emotional banner after first scroll on mobile
  useEffect(() => {
    if (!showWhyMatters) return;
    
    let hasScrolled = false;
    const handleScroll = () => {
      if (!hasScrolled && window.scrollY > 100) {
        hasScrolled = true;
        // Auto-dismiss after short delay when user scrolls
        setTimeout(() => {
          setShowWhyMatters(false);
          localStorage.setItem('browseWhyMattersDismissed', 'true');
        }, 2000);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showWhyMatters]);
  
  // Count active filters (including search query and AI results)
  const activeFilterCount = [
    sourceFilter !== 'all',
    stateFilter !== 'all',
    urgentOnly,
    genderFilter !== 'all',
    ageFilter !== 'all',
    raceFilter !== 'all',
    hairColorFilter !== 'all',
    eyeColorFilter !== 'all',
    heightFilter !== 'all',
    weightFilter !== 'all',
    cityFilter?.trim()?.length > 0,
    hasPhotoFilter !== 'all',
    yearMissingFilter !== 'all',
    searchQuery?.trim()?.length > 0,
    aiResults !== null
  ].filter(Boolean).length;
  
  // Compare state from localStorage
  const [selectedForCompare, setSelectedForCompare] = useState(() => {
    try {
      const stored = localStorage.getItem('compareCases');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Sync with localStorage changes (both same-tab custom events and cross-tab storage events)
  useEffect(() => {
    const handleCompareUpdate = (e) => {
      setSelectedForCompare(e.detail || []);
    };
    
    const handleStorageChange = (e) => {
      if (e.key === 'compareCases') {
        try {
          const parsed = e.newValue ? JSON.parse(e.newValue) : [];
          setSelectedForCompare(parsed);
        } catch {
          setSelectedForCompare([]);
        }
      }
    };
    
    window.addEventListener('compareCasesUpdated', handleCompareUpdate);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('compareCasesUpdated', handleCompareUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  // Track if we're in AI search mode to prevent re-fetching
  const [isAISearchMode, setIsAISearchMode] = useState(false);
  
  useEffect(() => {
    // Check for AI search query parameter on initial load
    const aiQuery = searchParams.get('q');
    const searchQuery = searchParams.get('search');
    
    if (aiQuery && !isAISearchMode) {
      // AI search from URL
      handleAISearch(aiQuery);
    } else if (searchQuery && !isAISearchMode) {
      // Regular search from URL
      handleSearch(searchQuery);
    } else if (!isAISearchMode && !aiResults) {
      // Only fetch all cases if not in AI search mode
      fetchAllCases();
    }
    fetchStats();
  }, [urgentOnly, sourceFilter, stateFilter, genderFilter, ageFilter, raceFilter, hairColorFilter, eyeColorFilter, heightFilter, weightFilter, cityFilter, hasPhotoFilter, yearMissingFilter, doeDiscrepancyOnly]);

  const fetchStats = async () => {
    try {
      const stats = await getExternalStats();
      // Transform API response to expected format
      const bySourceMap = {};
      if (stats.by_source && Array.isArray(stats.by_source)) {
        stats.by_source.forEach(item => {
          if (item._id) {
            bySourceMap[item._id] = item.count;
          }
        });
      }
      setExternalStats({
        total_cases: stats.total || 0,
        with_photo: stats.with_photo || 0,
        geocoded: stats.geocoded || 0,
        by_source: bySourceMap,
        by_case_type: stats.by_case_type || {}
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    // Per-option facet counts power "Texas (15,615)" labels in dropdowns.
    // Pass active filters so counts drill down (e.g., Hispanic count
    // within Texas, not the global Hispanic count).
    try {
      const facetParams = {};
      // Source filter — `community` and `ncmec_adults` are the only ones
      // not directly stored on the docs as `source=` values; community
      // toggles a different path entirely so we skip it here.
      if (sourceFilter !== 'all' && sourceFilter !== 'community') {
        facetParams.source = sourceFilter;
      }
      if (stateFilter !== 'all') facetParams.state = stateFilter;
      if (genderFilter !== 'all') facetParams.gender = genderFilter;
      if (raceFilter !== 'all') facetParams.race = raceFilter;
      if (hairColorFilter !== 'all') facetParams.hair_color = hairColorFilter;
      if (eyeColorFilter !== 'all') facetParams.eye_color = eyeColorFilter;
      if (hasPhotoFilter !== 'all') facetParams.has_photo = hasPhotoFilter;
      const f = await getBrowseFacets(facetParams);
      setFacets(f);
    } catch (error) {
      console.error('Error fetching facets:', error);
    }
  };

  // Format counts compactly (12,345 -> 12.3k) so they fit inside dropdown items.
  const fmtCount = (n) => {
    if (n === undefined || n === null) return '';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 10000) return `${Math.round(n / 1000)}k`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toLocaleString();
  };
  const facetCount = (group, key) => {
    const v = facets?.[group]?.[key];
    return typeof v === 'number' && v > 0 ? v : null;
  };

  // Helper to build filter params for API
  const buildFilterParams = () => {
    const params = { limit: CASES_PER_PAGE, skip: 0 };
    
    if (stateFilter !== 'all') params.state = stateFilter;
    if (sourceFilter !== 'all' && sourceFilter !== 'community') {
      params.source = sourceFilter;
    }
    if (genderFilter !== 'all') params.gender = genderFilter;
    if (raceFilter !== 'all') params.race = raceFilter;
    if (hairColorFilter !== 'all') params.hair_color = hairColorFilter;
    if (eyeColorFilter !== 'all') params.eye_color = eyeColorFilter;
    if (hasPhotoFilter !== 'all') params.has_photo = hasPhotoFilter === 'yes';
    if (cityFilter?.trim()) params.city = cityFilter.trim();
    if (doeDiscrepancyOnly) params.has_flag = 'DOE_NETWORK_FORENSIC_DISCREPANCY';
    
    // Height range mapping (in inches)
    if (heightFilter !== 'all') {
      const heightRanges = {
        'under_5ft': { min: 0, max: 59 },      // Under 5'0" (60 inches)
        '5ft_5ft4': { min: 60, max: 64 },      // 5'0" - 5'4"
        '5ft5_5ft8': { min: 65, max: 68 },     // 5'5" - 5'8"
        '5ft9_6ft': { min: 69, max: 72 },      // 5'9" - 6'0"
        'over_6ft': { min: 73, max: 96 }       // Over 6'0"
      };
      const range = heightRanges[heightFilter];
      if (range) {
        params.height_min = range.min;
        params.height_max = range.max;
      }
    }
    
    // Weight range mapping (in lbs)
    if (weightFilter !== 'all') {
      const weightRanges = {
        'under_100': { min: 0, max: 99 },
        '100_150': { min: 100, max: 150 },
        '150_200': { min: 150, max: 200 },
        '200_250': { min: 200, max: 250 },
        'over_250': { min: 250, max: 500 }
      };
      const range = weightRanges[weightFilter];
      if (range) {
        params.weight_min = range.min;
        params.weight_max = range.max;
      }
    }
    
    // Age range mapping
    if (ageFilter !== 'all') {
      const ageRanges = {
        'child': { min: 0, max: 12 },
        'teen': { min: 13, max: 17 },
        'young_adult': { min: 18, max: 25 },
        'adult': { min: 26, max: 64 },
        'senior': { min: 65, max: 120 }
      };
      const range = ageRanges[ageFilter];
      if (range) {
        params.age_min = range.min;
        params.age_max = range.max;
      }
    }
    
    // Year missing mapping
    if (yearMissingFilter !== 'all') {
      const currentYear = new Date().getFullYear();
      const yearRanges = {
        'last_30_days': { min: currentYear, max: currentYear },
        'last_year': { min: currentYear - 1, max: currentYear },
        '1_5_years': { min: currentYear - 5, max: currentYear - 1 },
        '5_10_years': { min: currentYear - 10, max: currentYear - 5 },
        '10_plus_years': { min: 1900, max: currentYear - 10 }
      };
      const range = yearRanges[yearMissingFilter];
      if (range) {
        params.year_missing_min = range.min;
        params.year_missing_max = range.max;
      }
    }
    
    return params;
  };

  const fetchAllCases = async () => {
    setLoading(true);
    setPage(0);
    setHasMore(true);
    try {
      // Fetch user-submitted cases
      const userParams = {};
      if (urgentOnly) userParams.is_urgent = true;
      
      // Fetch browse cases - use buildFilterParams to include all filters
      const browseParams = buildFilterParams();
      
      // Handle source filtering (community is special - not sent to API)
      if (sourceFilter !== 'all' && sourceFilter !== 'community') {
        browseParams.source = sourceFilter;
      }
      
      // Fetch both APIs, but don't fail if one fails
      let userData = [];
      let browseData = { cases: [], total: 0, has_more: false };
      
      try {
        console.log('[BrowseCases] Fetching cases with params:', { userParams, browseParams });
        const results = await Promise.allSettled([
          getCases(userParams),
          getBrowseCases(browseParams)
        ]);
        
        console.log('[BrowseCases] Promise.allSettled results:', results);
        
        if (results[0].status === 'fulfilled') {
          userData = results[0].value || [];
          console.log('[BrowseCases] getCases returned:', userData.length, 'cases');
        } else {
          console.warn('[BrowseCases] getCases failed:', results[0].reason);
        }
        
        if (results[1].status === 'fulfilled') {
          browseData = results[1].value || { cases: [], total: 0, has_more: false };
          console.log('[BrowseCases] getBrowseCases returned:', browseData.cases?.length, 'cases, total:', browseData.total);
        } else {
          console.warn('[BrowseCases] getBrowseCases failed:', results[1].reason);
        }
      } catch (e) {
        console.error('[BrowseCases] Promise.allSettled error:', e);
      }
      
      console.log('[BrowseCases] Setting state - userData:', userData.length, 'browseData.cases:', browseData.cases?.length);
      setCases(userData);
      // Browse API returns {cases: [], total, has_more, canonical_count, external_count}
      setExternalCases(browseData.cases || []);
      setHasMore(browseData.has_more || false);
      // Track filtered total separately so the count display reflects active filters
      setFilteredTotal(typeof browseData.total === 'number' ? browseData.total : null);
      setAiResults(null);
      setIsAISearchMode(false);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreCases = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    
    try {
      const nextPage = page + 1;
      // Use buildFilterParams to include all filters
      const browseParams = buildFilterParams();
      browseParams.skip = nextPage * CASES_PER_PAGE;
      
      // Handle source filtering (community is special - not sent to API)
      if (sourceFilter !== 'all' && sourceFilter !== 'community') {
        browseParams.source = sourceFilter;
      }
      
      const browseData = await getBrowseCases(browseParams);
      
      if (browseData.cases && browseData.cases.length > 0) {
        // Deduplicate: only add cases that aren't already in the list
        setExternalCases(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newCases = browseData.cases.filter(c => !existingIds.has(c.id));
          return [...prev, ...newCases];
        });
        setPage(nextPage);
        setHasMore(browseData.has_more || false);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more cases:', error);
      toast.error('Failed to load more cases');
    } finally {
      setLoadingMore(false);
    }
  };

  // Combine and filter cases based on source
  const getDisplayedCases = () => {
    let displayed = [];
    const seenIds = new Set();
    
    // Helper function to check if a case matches the current filters
    const matchesFilters = (c) => {
      // Gender filter
      if (genderFilter !== 'all') {
        const caseGender = (c.gender || '').toLowerCase();
        if (caseGender !== genderFilter.toLowerCase()) return false;
      }
      
      // Age filter
      if (ageFilter !== 'all') {
        const age = c.age || c.current_age || c.age_at_disappearance;
        if (age === undefined || age === null) return false;
        
        const ageRanges = {
          'child': { min: 0, max: 12 },
          'teen': { min: 13, max: 17 },
          'young_adult': { min: 18, max: 25 },
          'adult': { min: 26, max: 64 },
          'senior': { min: 65, max: 120 }
        };
        const range = ageRanges[ageFilter];
        if (range && (age < range.min || age > range.max)) return false;
      }
      
      // Race filter
      if (raceFilter !== 'all') {
        const caseRace = (c.race || c.ethnicity || '').toLowerCase();
        if (!caseRace.includes(raceFilter.toLowerCase())) return false;
      }
      
      // Hair color filter
      if (hairColorFilter !== 'all') {
        const hairColor = (c.hair_color || '').toLowerCase();
        if (!hairColor.includes(hairColorFilter.toLowerCase())) return false;
      }
      
      // Eye color filter
      if (eyeColorFilter !== 'all') {
        const eyeColor = (c.eye_color || '').toLowerCase();
        if (!eyeColor.includes(eyeColorFilter.toLowerCase())) return false;
      }
      
      // Has photo filter
      if (hasPhotoFilter !== 'all') {
        const hasPhoto = !!(c.photo_url || c.thumbnail_url || (c.photos && c.photos.length > 0));
        if (hasPhotoFilter === 'yes' && !hasPhoto) return false;
        if (hasPhotoFilter === 'no' && hasPhoto) return false;
      }
      
      // Year missing filter
      if (yearMissingFilter !== 'all') {
        const dateMissing = c.date_missing || c.missing_since;
        if (!dateMissing) return false;
        
        const year = parseInt(dateMissing.substring(0, 4));
        if (isNaN(year)) return false;
        
        const currentYear = new Date().getFullYear();
        const yearRanges = {
          'last_30_days': { min: currentYear, max: currentYear },
          'last_year': { min: currentYear - 1, max: currentYear },
          '1_5_years': { min: currentYear - 5, max: currentYear - 1 },
          '5_10_years': { min: currentYear - 10, max: currentYear - 5 },
          '10_plus_years': { min: 1900, max: currentYear - 10 }
        };
        const range = yearRanges[yearMissingFilter];
        if (range && (year < range.min || year > range.max)) return false;
      }
      
      // Height filter (client-side, uses height_inches if available)
      if (heightFilter !== 'all') {
        const heightInches = c.height_inches;
        if (!heightInches) return false;
        
        const heightRanges = {
          'under_5ft': { min: 0, max: 59 },
          '5ft_5ft4': { min: 60, max: 64 },
          '5ft5_5ft8': { min: 65, max: 68 },
          '5ft9_6ft': { min: 69, max: 72 },
          'over_6ft': { min: 73, max: 96 }
        };
        const range = heightRanges[heightFilter];
        if (range && (heightInches < range.min || heightInches > range.max)) return false;
      }
      
      // Weight filter (client-side, uses weight_lbs if available)
      if (weightFilter !== 'all') {
        const weightLbs = c.weight_lbs;
        if (!weightLbs) return false;
        
        const weightRanges = {
          'under_100': { min: 0, max: 99 },
          '100_150': { min: 100, max: 150 },
          '150_200': { min: 150, max: 200 },
          '200_250': { min: 200, max: 250 },
          'over_250': { min: 250, max: 500 }
        };
        const range = weightRanges[weightFilter];
        if (range && (weightLbs < range.min || weightLbs > range.max)) return false;
      }
      
      // City filter (client-side)
      if (cityFilter?.trim()) {
        const cityLower = cityFilter.toLowerCase().trim();
        const caseCity = (c.city || '').toLowerCase();
        const caseLocation = (c.last_seen_location || '').toLowerCase();
        if (!caseCity.includes(cityLower) && !caseLocation.includes(cityLower)) return false;
      }
      
      return true;
    };
    
    // Add community cases if applicable (with filter check).
    // Community cases lack reliable state/race fields, so omit them when
    // a state filter or a non-community external source filter is active.
    const showCommunity = (sourceFilter === 'all' && stateFilter === 'all') || sourceFilter === 'community';
    if (showCommunity) {
      for (const c of cases) {
        if (!seenIds.has(c.id) && matchesFilters(c)) {
          seenIds.add(c.id);
          displayed.push({ ...c, sourceType: 'community' });
        }
      }
    }
    
    // Filter external cases based on source filter
    let filteredExternal = externalCases;
    if (sourceFilter === 'ncmec') {
      filteredExternal = externalCases.filter(c => c.source === 'ncmec');
    } else if (sourceFilter === 'ncmec_adults') {
      filteredExternal = externalCases.filter(c => c.source === 'ncmec' && c.is_adult === true);
    } else if (sourceFilter === 'namus') {
      filteredExternal = externalCases.filter(c => c.source === 'namus');
    } else if (sourceFilter === 'namus_unidentified') {
      filteredExternal = externalCases.filter(c => c.source === 'namus_unidentified');
    } else if (sourceFilter === 'namus_unclaimed') {
      filteredExternal = externalCases.filter(c => c.source === 'namus_unclaimed' || c.source === 'cook_county_il');
    } else if (sourceFilter === 'charley') {
      filteredExternal = externalCases.filter(c => c.source === 'charley');
    } else if (sourceFilter === 'doenetwork') {
      filteredExternal = externalCases.filter(c => c.source === 'doenetwork');
    }
    
    // Add external cases to display for valid source filters (with deduplication and filter check)
    // Note: External cases are already filtered by the API, but we double-check here for safety
    const validExternalFilters = ['all', 'ncmec', 'ncmec_adults', 'namus', 'namus_unidentified', 'namus_unclaimed', 'charley', 'doenetwork'];
    if (validExternalFilters.includes(sourceFilter)) {
      for (const c of filteredExternal) {
        if (!seenIds.has(c.id) && matchesFilters(c)) {
          seenIds.add(c.id);
          displayed.push({ ...c, sourceType: c.source || 'external' });
        }
      }
    }
    
    return displayed;
  };

  const displayedCases = getDisplayedCases();

  // Helper: Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Smart sorting based on user preference and location
  const sortCases = (cases) => {
    return [...cases].sort((a, b) => {
      // Featured cases always first
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      
      // Apply user's sort preference
      switch (sortBy) {
        case 'nearest':
          if (userLocation) {
            const aLat = a.lat || a.latitude;
            const aLng = a.lng || a.longitude;
            const bLat = b.lat || b.latitude;
            const bLng = b.lng || b.longitude;
            
            if (aLat && aLng && bLat && bLng) {
              const distA = calculateDistance(userLocation.lat, userLocation.lng, aLat, aLng);
              const distB = calculateDistance(userLocation.lat, userLocation.lng, bLat, bLng);
              return distA - distB;
            }
            if (aLat && aLng && (!bLat || !bLng)) return -1;
            if ((!aLat || !aLng) && bLat && bLng) return 1;
          }
          break;
        case 'oldest':
          const oldDateA = new Date(a.last_seen_date || a.missing_since || a.date_found || 0);
          const oldDateB = new Date(b.last_seen_date || b.missing_since || b.date_found || 0);
          return oldDateA - oldDateB; // Oldest first
        case 'name_asc':
          const nameA = (a.name || a.full_name || '').toLowerCase();
          const nameB = (b.name || b.full_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        case 'name_desc':
          const nameA2 = (a.name || a.full_name || '').toLowerCase();
          const nameB2 = (b.name || b.full_name || '').toLowerCase();
          return nameB2.localeCompare(nameA2);
        case 'recent':
        default:
          const dateA = new Date(a.last_seen_date || a.missing_since || a.date_found || 0);
          const dateB = new Date(b.last_seen_date || b.missing_since || b.date_found || 0);
          return dateB - dateA; // Most recent first
      }
      return 0;
    });
  };

  // Get sort label for display
  const getSortLabel = () => {
    switch (sortBy) {
      case 'recent': return 'Newest first';
      case 'oldest': return 'Oldest first';
      case 'name_asc': return 'Name A-Z';
      case 'name_desc': return 'Name Z-A';
      case 'nearest': return userLocation ? 'Nearest first' : 'Nearest (needs location)';
      default: return 'Newest first';
    }
  };

  // Get filtered/displayed cases with smart sorting
  const filteredCases = sortCases(searchQuery ? displayedCases : displayedCases);
  
  // Tiered search state
  const [searchTiers, setSearchTiers] = useState({ tier1: 0, tier3: 0 });

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchQuery('');
      setAiResults(null);
      setIsAISearchMode(false);
      setSearchTiers({ tier1: 0, tier3: 0 });
      fetchAllCases();
      return;
    }
    
    setSearchQuery(query);
    setAiResults(null);
    setIsAISearchMode(false);
    setLoading(true);
    setSearchLoading(true);  // ← drives the orange button into "Searching..." state
                              //    and the spinner inside the SearchBar input
    
    // Detect if query looks like natural language (contains age patterns, location phrases, etc.)
    const isNaturalLanguage = /(\d{1,2}\s*year\s*old|\bage\s*\d|\b(boy|girl|man|woman|male|female)\b.*\b(in|from|missing)\b|\b(missing|found|disappeared)\b.*\b(in|from)\b)/i.test(query);
    
    let resultCount = 0;
    let searchMode = isNaturalLanguage ? 'smart' : 'standard';
    try {
      if (isNaturalLanguage) {
        // Use smart search for natural language queries
        console.log('Using smart search for natural language query:', query);
        const result = await smartSearch(query);
        
        if (result.results?.length > 0) {
          setExternalCases(result.results);
          setCases([]);
          setHasMore(false);
          setAiResults({
            interpretation: result.ai_interpretation,
            count: result.total,
            parsedFilters: result.parsed_filters
          });
          
          // Show what was parsed
          const filterCount = Object.keys(result.parsed_filters || {}).length;
          if (filterCount > 0) {
            toast.success(`Found ${result.total} cases matching your search`, {
              description: result.ai_interpretation
            });
          }
        } else {
          toast.info('No cases found matching your criteria');
          setExternalCases([]);
        }
        
        setSearchTiers({ tier1: result.total || 0, tier3: 0 });
        resultCount = result.total || (result.results?.length ?? 0);
      } else {
        // Use standard browse API with tiered search - name matches prioritized
        const browseData = await getBrowseCases({ search: query, limit: 200 });
        setExternalCases(browseData.cases || []);
        setCases([]); // Clear community cases when searching
        setHasMore(browseData.has_more || false);
        
        // Track tier counts for UI
        setSearchTiers({
          tier1: browseData.tier1_count || 0,
          tier3: browseData.tier3_count || 0
        });
        resultCount = (browseData.tier1_count || 0) + (browseData.tier3_count || 0) || (browseData.cases?.length ?? 0);
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
      // Fallback to standard search
      try {
        const browseData = await getBrowseCases({ search: query, limit: 200 });
        setExternalCases(browseData.cases || []);
        setCases([]);
      } catch (e) {
        console.error('Fallback search also failed:', e);
      }
    } finally {
      setLoading(false);
      setSearchLoading(false);
      // Funnel telemetry — fire once per submitted query
      trackSearchPerformed(query, resultCount, searchMode);
      // Smooth-scroll the page down to the results so the user immediately sees
      // that the search produced output. Without this, the results render far
      // below the fold and the page looks like nothing happened.
      requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const handleAISearch = async (query) => {
    setSearchLoading(true);
    setIsAISearchMode(true);
    setLoading(false); // Stop loading spinner
    try {
      const result = await aiSearch(query);
      if (result.results?.length > 0) {
        // AI search returns results from both community and external cases
        // Put them in externalCases since most are from external sources
        setExternalCases(result.results);
        setCases([]); // Clear community cases to show only AI results
        setAiResults({
          interpretation: result.ai_interpretation,
          count: result.results.length
        });
        setSourceFilter('all'); // Show all sources to display AI results
        toast.success(`AI found ${result.results.length} matching cases`);
      } else {
        toast.info(result.ai_interpretation || 'No matching cases found');
        setAiResults({
          interpretation: result.ai_interpretation,
          count: 0
        });
      }
    } catch (error) {
      toast.error('AI search failed. Showing all cases.');
      setIsAISearchMode(false);
      fetchAllCases();
    } finally {
      setSearchLoading(false);
      requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const handleSourceChange = (value) => {
    setSourceFilter(value);
    setSearchParams(prev => {
      if (value === 'all') prev.delete('source');
      else prev.set('source', value);
      return prev;
    });
  };

  const handleStateChange = (value) => {
    setStateFilter(value);
    setSearchParams(prev => {
      if (value === 'all') prev.delete('state');
      else prev.set('state', value);
      return prev;
    });
  };

  const toggleUrgent = () => {
    setUrgentOnly(!urgentOnly);
    setSearchParams(prev => {
      if (!urgentOnly) prev.set('urgent', 'true');
      else prev.delete('urgent');
      return prev;
    });
  };

  // Mobile AI toggle state
  const [mobileUseAI, setMobileUseAI] = useState(false);

  // Dynamic SEO based on filters
  const seoTitle = sourceFilter !== 'all' 
    ? `Browse ${sourceFilter.toUpperCase()} Missing Person Cases`
    : stateFilter !== 'all'
      ? `Missing Person Cases in ${stateFilter}`
      : 'Browse Missing Person Cases';

  return (
    <>
      <SEO
        title={seoTitle}
        description="Search 100,000+ missing person and unidentified remains cases from NamUs, NCMEC, Doe Network, and Charley Project. Filter by state, source, status. AI-powered search finds connections."
        url="/browse"
        keywords="missing persons database, search missing people, NamUs search, NCMEC database, unidentified remains, John Doe cases, missing person search"
      />
      <div className="min-h-screen py-4 md:py-8 lg:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button - Desktop only (mobile uses navbar) */}
          <button 
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
            className="hidden md:inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            data-testid="browse-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          {/* Mobile: Search Bar - Single button with AI toggle */}
          <div className="md:hidden -mx-4 px-4 pb-3 pt-2 bg-background border-b mb-2">
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                const query = e.target.elements.mobileSearch.value.trim();
                if (query) {
                  if (mobileUseAI) {
                    handleAISearch(query);
                  } else {
                    handleSearch(query);
                  }
                  if (showAIHint) dismissAIHint();
                }
              }}
              className="space-y-2"
            >
              {/* Search Input with inline button */}
              <div className="relative">
                {searchLoading ? (
                  <Loader2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-trust-blue-500 animate-spin pointer-events-none z-10" />
                ) : (
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
                )}
                <input
                  name="mobileSearch"
                  type="text"
                  disabled={searchLoading}
                  placeholder='Try: "16 year old boy missing in Charlotte NC"'
                  className="w-full h-11 pl-10 pr-24 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-trust-blue-500/30 focus:border-trust-blue-500 disabled:opacity-70"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={searchLoading}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-4 rounded-full bg-safety-orange-500 hover:bg-safety-orange-600 text-white text-xs font-semibold"
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </Button>
              </div>
              
              {/* AI Toggle + Loading Status - Enhanced visibility when enabled */}
              <div className="flex items-center justify-between px-1">
                <label className={`flex items-center gap-2 cursor-pointer select-none px-2 py-1 rounded-full transition-colors ${
                  mobileUseAI 
                    ? 'bg-trust-blue-50 dark:bg-trust-blue-950/30 border border-trust-blue-200 dark:border-trust-blue-800' 
                    : ''
                }`}>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={mobileUseAI}
                      onChange={(e) => setMobileUseAI(e.target.checked)}
                      className="sr-only peer"
                      disabled={searchLoading}
                    />
                    <div className={`w-4 h-4 border-2 rounded transition-colors flex items-center justify-center peer-disabled:opacity-50 ${
                      mobileUseAI 
                        ? 'bg-trust-blue-500 border-trust-blue-500' 
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
                    }`}>
                      {mobileUseAI && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs flex items-center gap-1 ${
                    mobileUseAI 
                      ? 'text-trust-blue-700 dark:text-trust-blue-300 font-medium' 
                      : 'text-slate-600 dark:text-slate-400'
                  }`}>
                    <Sparkles className={`w-3 h-3 ${mobileUseAI ? 'text-trust-blue-500' : 'text-slate-400'}`} />
                    {mobileUseAI ? 'Precision Mode' : 'AI-assisted matching'}
                  </span>
                </label>
                
                {searchLoading && (
                  <span className="text-xs text-trust-blue-600 dark:text-trust-blue-400 font-medium">
                    Finding matches...
                  </span>
                )}
              </div>
            </form>
          </div>
          
          {/* Mobile: Sticky Filter + Map Row */}
          <div className="md:hidden sticky top-0 z-40 bg-background/95 backdrop-blur-md -mx-4 px-4 py-2 border-b shadow-sm">
            <div className="flex items-center gap-2">
              <Button
                variant={activeFilterCount > 0 ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterDrawerOpen(true)}
                className={`rounded-full text-xs gap-1.5 h-8 flex-1 ${activeFilterCount > 0 ? 'bg-trust-blue-500 hover:bg-trust-blue-600' : ''}`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] bg-white/20 text-white">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
              
              <Link to="/map" className="flex-1">
                <Button variant="outline" size="sm" className="rounded-full text-xs gap-1.5 w-full h-8">
                  <Map className="w-3.5 h-3.5" />
                  Map
                </Button>
              </Link>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMobileInfo(!showMobileInfo)}
                className="rounded-full text-xs h-8 w-8 p-0"
              >
                <Info className="w-3.5 h-3.5" />
              </Button>
              
              {/* Case count badge */}
              <div className="text-xs text-muted-foreground whitespace-nowrap">
                {filteredCases.length} cases
              </div>
            </div>
          </div>

          {/* Mobile: "Why This Matters" Emotional Banner */}
          {showWhyMatters && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mb-3"
            >
              <div className="relative bg-gradient-to-r from-trust-blue-50 to-hope-green-50 dark:from-trust-blue-950/30 dark:to-hope-green-950/30 rounded-xl p-3 border border-trust-blue-100 dark:border-trust-blue-900/50">
                <button
                  onClick={dismissWhyMatters}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-white/70 dark:bg-black/30 hover:bg-white dark:hover:bg-black/50 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <p className="text-sm text-foreground/90 pr-7">
                  <span className="font-semibold">Every profile is a real person</span> — one share could bring them home.
                </p>
              </div>
            </motion.div>
          )}

          {/* Mobile: Collapsible Info & Stats */}
          <Collapsible open={showMobileInfo} onOpenChange={setShowMobileInfo} className="md:hidden mb-4">
            <CollapsibleContent className="space-y-4">
              <div className="text-center py-2">
                <h1 className="font-heading text-xl font-bold mb-1">
                  Browse Missing Person Cases
                </h1>
                <p className="text-xs text-muted-foreground">
                  Each case represents a real person and a family still searching.
                </p>
              </div>
              
              {/* Compact Stats Grid */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-safety-orange-50 dark:bg-safety-orange-950/20 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-safety-orange-600">
                    {externalStats?.total_cases || externalCases.length}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Total</div>
                </div>
                <div className="bg-trust-blue-50 dark:bg-trust-blue-950/20 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-trust-blue-600">
                    {externalStats?.by_source?.ncmec || 0}
                  </div>
                  <div className="text-[10px] text-muted-foreground">NCMEC</div>
                </div>
                <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-green-600">
                    {externalStats?.by_source?.namus || 0}
                  </div>
                  <div className="text-[10px] text-muted-foreground">NamUs</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-purple-600">
                    {externalStats?.by_source?.namus_unidentified || 0}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Unident.</div>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground text-center pb-2">
                Search 62,000+ cases from NamUs, NCMEC, Charley Project & Doe Network across all 50 states.
              </p>
            </CollapsibleContent>
          </Collapsible>

          {/* Desktop: Original Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="hidden md:block text-center mb-10"
          >
            {/* Page Primer */}
            <p className="text-xs text-muted-foreground/70 mb-4 italic">
              Start broad, then narrow your search. Many meaningful connections appear only after filtering by location, time frame, or traits.
            </p>
            
            <h1 className="font-heading text-3xl lg:text-4xl font-bold mb-2">
              Explore Missing Person Cases
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-2">
              Search across 62,000+ missing, unidentified, and unclaimed cases from multiple trusted sources — all in one place.
            </p>
            <p className="text-sm text-muted-foreground/70 max-w-xl mx-auto mb-8">
              Use filters, maps, and AI-assisted search to explore cases and uncover potential connections.
            </p>

            <SearchBar 
              onSearch={handleSearch}
              onAISearch={handleAISearch}
              isLoading={searchLoading}
              placeholder='Try: "16 year old boy missing in Charlotte NC"'
            />
            
            {/* Search by Photo Option */}
            <div className="mt-4 flex items-center justify-center gap-2 text-sm">
              <span className="text-muted-foreground">Want to search by photo instead?</span>
              <Link 
                to="/facial-search" 
                className="inline-flex items-center gap-1.5 text-trust-blue-600 hover:text-trust-blue-700 dark:text-trust-blue-400 dark:hover:text-trust-blue-300 font-medium transition-colors"
              >
                <Camera className="w-4 h-4" />
                Search by Photo
              </Link>
            </div>
            
            {/* Search Helper Text */}
            <p className="text-xs text-muted-foreground/60 mt-3 max-w-lg mx-auto">
              You can search by physical traits, last known location, time frame, or identifying details.
            </p>
          </motion.div>

          {/* Desktop: Stats Banner - CLICKABLE FILTERS */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="hidden md:block mb-8"
        >
          <p className="text-sm font-medium text-muted-foreground mb-3 text-center">Cases by Source</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className={`bg-safety-orange-50 dark:bg-safety-orange-950/20 border-safety-orange-200 dark:border-safety-orange-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${sourceFilter === 'all' ? 'ring-2 ring-safety-orange-500' : ''}`}
            onClick={() => handleSourceChange('all')}
            data-testid="stat-total"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-safety-orange-600">
                {externalStats?.total_cases || externalCases.length}
              </div>
              <div className="text-sm text-muted-foreground">Total Cases Indexed</div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-trust-blue-50 dark:bg-trust-blue-950/20 border-trust-blue-200 dark:border-trust-blue-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${sourceFilter === 'ncmec' ? 'ring-2 ring-trust-blue-500' : ''}`}
            onClick={() => handleSourceChange('ncmec')}
            data-testid="stat-ncmec"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-trust-blue-600">
                {externalStats?.by_source?.ncmec || 0}
              </div>
              <div className="text-sm text-muted-foreground">NCMEC</div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${sourceFilter === 'namus' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleSourceChange('namus')}
            data-testid="stat-namus"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {externalStats?.by_source?.namus || 0}
              </div>
              <div className="text-sm text-muted-foreground">NamUs — Missing</div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${sourceFilter === 'namus_unidentified' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => handleSourceChange('namus_unidentified')}
            data-testid="stat-unidentified"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">
                {externalStats?.by_source?.namus_unidentified || 0}
              </div>
              <div className="text-sm text-muted-foreground">Unidentified Persons</div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${sourceFilter === 'charley' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => handleSourceChange('charley')}
            data-testid="stat-charley"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">
                {externalStats?.by_source?.charley || 0}
              </div>
              <div className="text-sm text-muted-foreground">Charley Project</div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-cyan-50 dark:bg-cyan-950/20 border-cyan-200 dark:border-cyan-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${sourceFilter === 'doenetwork' ? 'ring-2 ring-cyan-500' : ''}`}
            onClick={() => handleSourceChange('doenetwork')}
            data-testid="stat-doenetwork"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-cyan-600">
                {externalStats?.by_source?.doenetwork || 0}
              </div>
              <div className="text-sm text-muted-foreground">Doe Network</div>
            </CardContent>
          </Card>
          <Card 
            className={`bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${sourceFilter === 'namus_unclaimed' ? 'ring-2 ring-rose-500' : ''}`}
            onClick={() => handleSourceChange('namus_unclaimed')}
            data-testid="stat-unclaimed"
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-rose-600">
                {(externalStats?.by_source?.namus_unclaimed || 0) + (externalStats?.by_source?.cook_county_il || 0)}
              </div>
              <div className="text-sm text-muted-foreground">Unclaimed Persons</div>
            </CardContent>
          </Card>
          </div>
          <p className="text-xs text-muted-foreground/60 text-center mt-3">
            Sources are continuously updated as new cases and records become available.
          </p>
        </motion.div>

        {/* AI Results Banner */}
        {aiResults && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="bg-trust-blue-50 dark:bg-trust-blue-950/30 border-trust-blue-200 dark:border-trust-blue-800">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-trust-blue-700 dark:text-trust-blue-400">
                    AI Search Results: {aiResults.count} cases found
                  </p>
                  <p className="text-sm text-trust-blue-600 dark:text-trust-blue-500">
                    {aiResults.interpretation}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchAllCases}
                  className="border-trust-blue-300 text-trust-blue-700 hover:bg-trust-blue-100"
                >
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Compare Mode Banner */}
        {selectedForCompare.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <Scale className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-700 dark:text-purple-400">
                      {selectedForCompare.length} case{selectedForCompare.length > 1 ? 's' : ''} selected for comparison
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedForCompare.map(c => (
                      <Badge key={c.id} variant="secondary" className="flex items-center gap-1">
                        {c.name?.split(' ')[0]}
                        <button 
                          onClick={() => {
                            const updated = selectedForCompare.filter(sc => sc.id !== c.id);
                            setSelectedForCompare(updated);
                            localStorage.setItem('compareCases', JSON.stringify(updated));
                            window.dispatchEvent(new CustomEvent('compareCasesUpdated', { detail: updated }));
                          }} 
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {selectedForCompare.length >= 2 && (
                      <Link to={`/compare?ids=${selectedForCompare.map(c => c.id).join(',')}`}>
                        <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white rounded-full">
                          Compare Now
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        setSelectedForCompare([]);
                        localStorage.removeItem('compareCases');
                        window.dispatchEvent(new CustomEvent('compareCasesUpdated', { detail: [] }));
                      }}
                      className="text-purple-600"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Filters - Desktop inline, Mobile via quick action button */}
        <div ref={resultsRef} className="mb-8 scroll-mt-20" data-testid="browse-results-section">
          {/* Filter Framing Line */}
          <p className="hidden md:block text-xs text-muted-foreground/60 mb-3">
            Narrow results by location, status, urgency, or source to focus your search.
          </p>
          
          <div className="hidden md:flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Filters:</span>
              </div>

            <Select value={sourceFilter} onValueChange={handleSourceChange}>
              <SelectTrigger className="w-44 rounded-full" data-testid="source-filter">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">All Sources</span>
                </SelectItem>
                <SelectItem value="ncmec">
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-trust-blue-500" />
                    NCMEC (Official)
                    {facetCount('by_source', 'ncmec') && <span className="text-xs text-muted-foreground ml-1">({fmtCount(facetCount('by_source','ncmec'))})</span>}
                  </span>
                </SelectItem>
                <SelectItem value="ncmec_adults">
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-trust-blue-400" />
                    NCMEC Adults (18+)
                    {facetCount('by_source', 'ncmec_adults') && <span className="text-xs text-muted-foreground ml-1">({fmtCount(facetCount('by_source','ncmec_adults'))})</span>}
                  </span>
                </SelectItem>
                <SelectItem value="namus">
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-green-500" />
                    NamUs Missing
                    {facetCount('by_source', 'namus') && <span className="text-xs text-muted-foreground ml-1">({fmtCount(facetCount('by_source','namus'))})</span>}
                  </span>
                </SelectItem>
                <SelectItem value="namus_unidentified">
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-purple-500" />
                    NamUs Unidentified
                    {facetCount('by_source', 'namus_unidentified') && <span className="text-xs text-muted-foreground ml-1">({fmtCount(facetCount('by_source','namus_unidentified'))})</span>}
                  </span>
                </SelectItem>
                <SelectItem value="charley">
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-amber-500" />
                    Charley Project
                    {facetCount('by_source', 'charley') && <span className="text-xs text-muted-foreground ml-1">({fmtCount(facetCount('by_source','charley'))})</span>}
                  </span>
                </SelectItem>
                <SelectItem value="doenetwork">
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-cyan-500" />
                    Doe Network
                    {facetCount('by_source', 'doenetwork') && <span className="text-xs text-muted-foreground ml-1">({fmtCount(facetCount('by_source','doenetwork'))})</span>}
                  </span>
                </SelectItem>
                <SelectItem value="namus_unclaimed">
                  <span className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-rose-500" />
                    NamUs Unclaimed
                    {facetCount('by_source', 'namus_unclaimed') && <span className="text-xs text-muted-foreground ml-1">({fmtCount(facetCount('by_source','namus_unclaimed'))})</span>}
                  </span>
                </SelectItem>
                <SelectItem value="community">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-500" />
                    Community Reports
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={stateFilter} onValueChange={handleStateChange}>
              <SelectTrigger className="w-44 rounded-full" data-testid="state-filter">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map(state => {
                  const c = facetCount('by_state', state);
                  return (
                    <SelectItem key={state} value={state}>
                      <span className="flex items-center gap-2">
                        {state}
                        {c && <span className="text-xs text-muted-foreground ml-1">({fmtCount(c)})</span>}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Button
              variant={urgentOnly ? 'default' : 'outline'}
              size="sm"
              onClick={toggleUrgent}
              className={`rounded-full ${urgentOnly ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
              data-testid="urgent-filter"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Urgent Only
            </Button>

            {user?.tier === 'pro' && (
              <Button
                variant={doeDiscrepancyOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDoeDiscrepancyOnly(v => !v)}
                className={`rounded-full ${doeDiscrepancyOnly ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                data-testid="doe-discrepancy-filter"
                title="Doe Network reports dental/DNA available — NamUs does not. Needs agency verification."
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Doe Forensic Discrepancy
              </Button>
            )}

            <Button
              variant={showAdvancedFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="rounded-full"
            >
              <Filter className="w-4 h-4 mr-2" />
              More Filters
              {(genderFilter !== 'all' || ageFilter !== 'all' || raceFilter !== 'all' || 
                hairColorFilter !== 'all' || eyeColorFilter !== 'all' || hasPhotoFilter !== 'all' || 
                yearMissingFilter !== 'all' || heightFilter !== 'all' || weightFilter !== 'all' ||
                cityFilter?.trim()?.length > 0) && (
                <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {[genderFilter, ageFilter, raceFilter, hairColorFilter, eyeColorFilter, hasPhotoFilter, yearMissingFilter, heightFilter, weightFilter]
                    .filter(f => f !== 'all').length + (cityFilter?.trim()?.length > 0 ? 1 : 0)}
                </Badge>
              )}
            </Button>

            {/* Clear Filters Button - appears when any filter is active */}
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSourceFilter('all');
                  setStateFilter('all');
                  setUrgentOnly(false);
                  setGenderFilter('all');
                  setAgeFilter('all');
                  setRaceFilter('all');
                  setHairColorFilter('all');
                  setEyeColorFilter('all');
                  setHeightFilter('all');
                  setWeightFilter('all');
                  setCityFilter('');
                  setHasPhotoFilter('all');
                  setYearMissingFilter('all');
                  setSearchParams(new URLSearchParams());
                  setSearchQuery('');
                  setAiResults(null);
                  setIsAISearchMode(false);
                  fetchAllCases();
                }}
                className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground" data-testid="browse-result-count">
                {searchQuery ? (
                  <>{filteredCases.length.toLocaleString()} results for "<span className="font-medium text-foreground">{searchQuery}</span>"</>
                ) : activeFilterCount > 0 && filteredTotal !== null ? (
                  <>{filteredTotal.toLocaleString()} matching cases</>
                ) : (
                  <>{externalStats?.total_cases?.toLocaleString() || filteredCases.length} matching cases</>
                )}
              </span>
              <span className="text-[10px] text-muted-foreground/50">
                {searchQuery ? 'Showing name matches first' : 'Results update as filters change'}
              </span>
            </div>
            {/* Sort Dropdown - Desktop */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-[150px] text-sm border-slate-200 dark:border-slate-800">
                <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
                <SelectItem value="name_asc">Name A-Z</SelectItem>
                <SelectItem value="name_desc">Name Z-A</SelectItem>
                <SelectItem value="nearest" disabled={!userLocation}>
                  {userLocation ? 'Nearest first' : 'Nearest (enable location)'}
                </SelectItem>
              </SelectContent>
            </Select>
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none ${viewMode === 'grid' ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none ${viewMode === 'list' ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
          </div>
          
          {/* Mobile: Case count shown separately with sort dropdown */}
          <div className="md:hidden flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {searchQuery ? (
                  <>{filteredCases.length} for "{searchQuery}"</>
                ) : (
                  <>{externalStats?.total_cases?.toLocaleString() || filteredCases.length} matching</>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Sort Dropdown - Mobile */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-8 w-[130px] text-xs border-slate-200 dark:border-slate-800">
                  <ArrowUpDown className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name A-Z</SelectItem>
                  <SelectItem value="name_desc">Name Z-A</SelectItem>
                  <SelectItem value="nearest" disabled={!userLocation}>
                    {userLocation ? 'Nearest first' : 'Nearest (enable location)'}
                  </SelectItem>
                </SelectContent>
              </Select>
              {/* View Mode Toggle */}
              <div className="flex rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`rounded-none px-2 h-8 ${viewMode === 'grid' ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                  onClick={() => setViewMode('grid')}
                  data-testid="view-mode-grid"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`rounded-none px-2 h-8 ${viewMode === 'list' ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
                  onClick={() => setViewMode('list')}
                  data-testid="view-mode-list"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Mobile: Quick Filter Chips */}
          <div className="md:hidden mb-4">
            <QuickFilterBar
              genderFilter={genderFilter}
              setGenderFilter={setGenderFilter}
              ageFilter={ageFilter}
              setAgeFilter={setAgeFilter}
              yearMissingFilter={yearMissingFilter}
              setYearMissingFilter={setYearMissingFilter}
              onClearAll={() => {
                setGenderFilter('all');
                setAgeFilter('all');
                setYearMissingFilter('all');
              }}
            />
          </div>
          
          <p className="hidden md:block text-xs text-muted-foreground mt-3">
            Filters help narrow cases by physical traits, timeframes, and location — details often critical to recognition.
          </p>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <Card className="border-trust-blue-200 dark:border-trust-blue-800 bg-slate-50 dark:bg-slate-900/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm">Advanced Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setGenderFilter('all');
                      setAgeFilter('all');
                      setRaceFilter('all');
                      setHairColorFilter('all');
                      setEyeColorFilter('all');
                      setHeightFilter('all');
                      setWeightFilter('all');
                      setCityFilter('');
                      setHasPhotoFilter('all');
                      setYearMissingFilter('all');
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {/* Gender Filter */}
                  <Select value={genderFilter} onValueChange={setGenderFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Genders</SelectItem>
                      <SelectItem value="male">Male{facetCount('by_gender','male') ? ` (${fmtCount(facetCount('by_gender','male'))})` : ''}</SelectItem>
                      <SelectItem value="female">Female{facetCount('by_gender','female') ? ` (${fmtCount(facetCount('by_gender','female'))})` : ''}</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Age Range Filter */}
                  <Select value={ageFilter} onValueChange={setAgeFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Age Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ages</SelectItem>
                      <SelectItem value="child">Child (0-12)</SelectItem>
                      <SelectItem value="teen">Teen (13-17)</SelectItem>
                      <SelectItem value="young_adult">Young Adult (18-25)</SelectItem>
                      <SelectItem value="adult">Adult (26-64)</SelectItem>
                      <SelectItem value="senior">Senior (65+)</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Year Missing Filter */}
                  <Select value={yearMissingFilter} onValueChange={setYearMissingFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Year Missing" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                      <SelectItem value="last_year">Last Year</SelectItem>
                      <SelectItem value="1_5_years">1-5 Years Ago</SelectItem>
                      <SelectItem value="5_10_years">5-10 Years Ago</SelectItem>
                      <SelectItem value="10_plus_years">10+ Years Ago</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Race/Ethnicity Filter */}
                  <Select value={raceFilter} onValueChange={setRaceFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Race/Ethnicity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Races</SelectItem>
                      <SelectItem value="white">White{facetCount('by_race','white') ? ` (${fmtCount(facetCount('by_race','white'))})` : ''}</SelectItem>
                      <SelectItem value="black">Black{facetCount('by_race','black') ? ` (${fmtCount(facetCount('by_race','black'))})` : ''}</SelectItem>
                      <SelectItem value="hispanic">Hispanic/Latino{facetCount('by_race','hispanic') ? ` (${fmtCount(facetCount('by_race','hispanic'))})` : ''}</SelectItem>
                      <SelectItem value="asian">Asian{facetCount('by_race','asian') ? ` (${fmtCount(facetCount('by_race','asian'))})` : ''}</SelectItem>
                      <SelectItem value="native">Native American{facetCount('by_race','native') ? ` (${fmtCount(facetCount('by_race','native'))})` : ''}</SelectItem>
                      <SelectItem value="pacific">Pacific Islander</SelectItem>
                      <SelectItem value="biracial">Biracial/Multiracial</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Hair Color Filter */}
                  <Select value={hairColorFilter} onValueChange={setHairColorFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Hair Color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Hair Colors</SelectItem>
                      <SelectItem value="black">Black{facetCount('by_hair_color','black') ? ` (${fmtCount(facetCount('by_hair_color','black'))})` : ''}</SelectItem>
                      <SelectItem value="brown">Brown{facetCount('by_hair_color','brown') ? ` (${fmtCount(facetCount('by_hair_color','brown'))})` : ''}</SelectItem>
                      <SelectItem value="blonde">Blonde{facetCount('by_hair_color','blonde') ? ` (${fmtCount(facetCount('by_hair_color','blonde'))})` : ''}</SelectItem>
                      <SelectItem value="red">Red{facetCount('by_hair_color','red') ? ` (${fmtCount(facetCount('by_hair_color','red'))})` : ''}</SelectItem>
                      <SelectItem value="gray">Gray/White{facetCount('by_hair_color','gray') ? ` (${fmtCount(facetCount('by_hair_color','gray'))})` : ''}</SelectItem>
                      <SelectItem value="bald">Bald</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Eye Color Filter */}
                  <Select value={eyeColorFilter} onValueChange={setEyeColorFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Eye Color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Eye Colors</SelectItem>
                      <SelectItem value="brown">Brown{facetCount('by_eye_color','brown') ? ` (${fmtCount(facetCount('by_eye_color','brown'))})` : ''}</SelectItem>
                      <SelectItem value="blue">Blue{facetCount('by_eye_color','blue') ? ` (${fmtCount(facetCount('by_eye_color','blue'))})` : ''}</SelectItem>
                      <SelectItem value="green">Green{facetCount('by_eye_color','green') ? ` (${fmtCount(facetCount('by_eye_color','green'))})` : ''}</SelectItem>
                      <SelectItem value="hazel">Hazel{facetCount('by_eye_color','hazel') ? ` (${fmtCount(facetCount('by_eye_color','hazel'))})` : ''}</SelectItem>
                      <SelectItem value="gray">Gray</SelectItem>
                      <SelectItem value="black">Black</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Has Photo Filter */}
                  <Select value={hasPhotoFilter} onValueChange={setHasPhotoFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <SelectValue placeholder="Has Photo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cases</SelectItem>
                      <SelectItem value="yes">With Photo{facetCount('by_photo','yes') ? ` (${fmtCount(facetCount('by_photo','yes'))})` : ''}</SelectItem>
                      <SelectItem value="no">Without Photo{facetCount('by_photo','no') ? ` (${fmtCount(facetCount('by_photo','no'))})` : ''}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Second Row: Physical Characteristics & Location */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                  {/* Height Filter */}
                  <Select value={heightFilter} onValueChange={setHeightFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <div className="flex items-center gap-1.5">
                        <Ruler className="w-3.5 h-3.5 text-muted-foreground" />
                        <SelectValue placeholder="Height" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Heights</SelectItem>
                      <SelectItem value="under_5ft">Under 5'0"</SelectItem>
                      <SelectItem value="5ft_5ft4">5'0" - 5'4"</SelectItem>
                      <SelectItem value="5ft5_5ft8">5'5" - 5'8"</SelectItem>
                      <SelectItem value="5ft9_6ft">5'9" - 6'0"</SelectItem>
                      <SelectItem value="over_6ft">Over 6'0"</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Weight Filter */}
                  <Select value={weightFilter} onValueChange={setWeightFilter}>
                    <SelectTrigger className="w-full text-sm">
                      <div className="flex items-center gap-1.5">
                        <Weight className="w-3.5 h-3.5 text-muted-foreground" />
                        <SelectValue placeholder="Weight" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Weights</SelectItem>
                      <SelectItem value="under_100">Under 100 lbs</SelectItem>
                      <SelectItem value="100_150">100 - 150 lbs</SelectItem>
                      <SelectItem value="150_200">150 - 200 lbs</SelectItem>
                      <SelectItem value="200_250">200 - 250 lbs</SelectItem>
                      <SelectItem value="over_250">Over 250 lbs</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* City Filter */}
                  <div className="col-span-2 md:col-span-2 lg:col-span-3">
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                      <Input
                        type="text"
                        placeholder="Filter by city (e.g., Los Angeles)"
                        value={cityFilter}
                        onChange={(e) => setCityFilter(e.target.value)}
                        className="pl-9 h-10 text-sm"
                        data-testid="city-filter"
                      />
                      {cityFilter && (
                        <button
                          onClick={() => setCityFilter('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700"
                        >
                          <X className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Desktop: "Why This Matters" Micro-Message */}
        {showWhyMatters && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="hidden md:block mb-6"
          >
            <div className="relative bg-gradient-to-r from-trust-blue-50 to-hope-green-50 dark:from-trust-blue-950/30 dark:to-hope-green-950/30 rounded-xl p-4 border border-trust-blue-100 dark:border-trust-blue-900/50">
              <button
                onClick={dismissWhyMatters}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-white/60 dark:bg-black/20 hover:bg-white dark:hover:bg-black/40 transition-colors"
                aria-label="Dismiss message"
              >
                <X className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              </button>
              <div className="pr-8">
                <p className="text-sm text-foreground/90 leading-relaxed">
                  <span className="font-semibold">Every profile represents a real person</span> — and a family still searching for answers.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Even one shared case can bring someone home.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Cases Grid/List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <LogoIcon size={64} className="animate-pulse mb-4" />
            <p className="text-muted-foreground">Searching cases...</p>
          </div>
        ) : filteredCases.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">No Cases Found</h3>
            <p className="text-muted-foreground mb-4">
              {sourceFilter !== 'all' || stateFilter !== 'all' || genderFilter !== 'all' 
                ? 'Try adjusting your filters or search terms.'
                : 'Having trouble loading cases. Please try again.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={fetchAllCases} variant="outline" className="rounded-full">
                Reset Filters
              </Button>
              <Button onClick={() => { setLoading(true); fetchAllCases(); }} className="rounded-full">
                Retry
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Tier 1: Name matches (shown first) */}
            {searchQuery && searchTiers.tier1 > 0 && (
              <div className="mb-2">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span>{searchTiers.tier1} name {searchTiers.tier1 === 1 ? 'match' : 'matches'}</span>
                </p>
              </div>
            )}
            
            {/* Grid View - Search mode with tiered results */}
            {searchQuery && viewMode === 'grid' && (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 lg:gap-8">
                {/* Render Tier 1 cases first */}
                {filteredCases.filter(c => !c.match_tier || c.match_tier === 1).map((person, index) => (
                  <MissingPersonCard key={`${person.sourceType}-${person.id}`} person={person} index={index} />
                ))}
              </div>
            )}
            
            {/* List View - Search mode with tiered results */}
            {searchQuery && viewMode === 'list' && (
              <div className="bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                {filteredCases.filter(c => !c.match_tier || c.match_tier === 1).map((person) => (
                  <CaseListItem key={`list-${person.sourceType}-${person.id}`} person={person} />
                ))}
              </div>
            )}
            
            {/* Tier 3 Divider: Other matches in text/location */}
            {searchQuery && searchTiers.tier3 > 0 && (
              <div className="my-8 flex items-center gap-4">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-slate-400" />
                  <span>Other cases mentioning &ldquo;{searchQuery}&rdquo; in details</span>
                </p>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
            )}
            
            {/* Render Tier 3 cases below divider */}
            {searchQuery && searchTiers.tier3 > 0 && viewMode === 'grid' && (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 lg:gap-8">
                {filteredCases.filter(c => c.match_tier === 3).map((person, index) => (
                  <MissingPersonCard key={`tier3-${person.sourceType}-${person.id}`} person={person} index={index} />
                ))}
              </div>
            )}
            
            {searchQuery && searchTiers.tier3 > 0 && viewMode === 'list' && (
              <div className="bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                {filteredCases.filter(c => c.match_tier === 3).map((person) => (
                  <CaseListItem key={`tier3-list-${person.sourceType}-${person.id}`} person={person} />
                ))}
              </div>
            )}
            
            {/* Non-search mode: render all cases normally */}
            {!searchQuery && viewMode === 'grid' && (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 lg:gap-8">
                {filteredCases.map((person, index) => (
                  <MissingPersonCard key={`${person.sourceType}-${person.id}`} person={person} index={index} />
                ))}
              </div>
            )}
            
            {!searchQuery && viewMode === 'list' && (
              <div className="bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                {filteredCases.map((person) => (
                  <CaseListItem key={`list-${person.sourceType}-${person.id}`} person={person} />
                ))}
              </div>
            )}
            
            {/* Load More Button */}
            {hasMore && !aiResults && (
              <div className="flex flex-col items-center gap-3 mt-8">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredCases.length} cases — thousands more need visibility
                </p>
                <Button
                  onClick={loadMoreCases}
                  disabled={loadingMore}
                  variant="outline"
                  size="lg"
                  className="rounded-full px-8"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>Load More Cases</>
                  )}
                </Button>
              </div>
            )}
            
            {/* Anonymous Tip CTA */}
            <div className="flex flex-col items-center gap-3 mt-12 pt-8 border-t border-slate-200 dark:border-slate-800">
              <p className="text-sm text-muted-foreground">
                Have information about any of these cases?
              </p>
              <Link to="/tips">
                <Button 
                  variant="outline" 
                  className="rounded-full px-6 border-safety-orange-300 text-safety-orange-600 hover:bg-safety-orange-50 hover:text-safety-orange-700 dark:border-safety-orange-700 dark:text-safety-orange-400 dark:hover:bg-safety-orange-950"
                >
                  Submit an Anonymous Tip
                </Button>
              </Link>
            </div>
            
            {/* Upgrade Nudge - Show after search results */}
            {searchQuery && filteredCases.length > 5 && (
              <SearchResultsNudge relatedCount={Math.min(filteredCases.length, 10)} />
            )}
            
            {/* Footer Nudge */}
            <FooterNudge />
          </>
        )}
      </div>
    </div>
    
    {/* Mobile Filter Bottom Sheet */}
    <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="pb-2 border-b">
          <div className="flex items-center justify-between">
            <DrawerTitle className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-trust-blue-500" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary">{activeFilterCount} active</Badge>
              )}
            </DrawerTitle>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSourceFilter('all');
                  setStateFilter('all');
                  setUrgentOnly(false);
                  setGenderFilter('all');
                  setAgeFilter('all');
                  setRaceFilter('all');
                  setHairColorFilter('all');
                  setEyeColorFilter('all');
                  setHasPhotoFilter('all');
                  setYearMissingFilter('all');
                }}
                className="text-xs text-red-500 hover:text-red-600"
              >
                Clear All
              </Button>
            )}
          </div>
        </DrawerHeader>
        
        <div className="overflow-y-auto px-4 py-4 space-y-6" style={{ maxHeight: 'calc(85vh - 120px)' }}>
          {/* Sort Section - Smart default */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              Sort By
            </h3>
            <div className="flex gap-2">
              <Button
                variant={sortBy === 'recent' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('recent')}
                className={`flex-1 rounded-full text-xs ${sortBy === 'recent' ? 'bg-trust-blue-500 hover:bg-trust-blue-600' : ''}`}
              >
                Most Recent
              </Button>
              <Button
                variant={sortBy === 'nearest' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  if (!userLocation) {
                    // Try to get location
                    navigator.geolocation?.getCurrentPosition(
                      (pos) => {
                        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                        setSortBy('nearest');
                        toast.success('Sorting by nearest location');
                      },
                      () => toast.error('Location access needed for nearby sort')
                    );
                  } else {
                    setSortBy('nearest');
                  }
                }}
                className={`flex-1 rounded-full text-xs ${sortBy === 'nearest' ? 'bg-trust-blue-500 hover:bg-trust-blue-600' : ''}`}
              >
                {userLocation ? 'Nearest' : '📍 Nearest'}
              </Button>
            </div>
            {sortBy === 'nearest' && userLocation && (
              <p className="text-[10px] text-muted-foreground mt-2 text-center">
                Showing cases closest to your location
              </p>
            )}
          </div>

          {/* Source Section */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Database className="w-3.5 h-3.5" />
              Source
            </h3>
            <Select value={sourceFilter} onValueChange={handleSourceChange}>
              <SelectTrigger className="rounded-full">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="ncmec">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    NCMEC (Official){facetCount('by_source','ncmec') ? ` (${fmtCount(facetCount('by_source','ncmec'))})` : ''}
                  </span>
                </SelectItem>
                <SelectItem value="namus">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    NamUs Missing{facetCount('by_source','namus') ? ` (${fmtCount(facetCount('by_source','namus'))})` : ''}
                  </span>
                </SelectItem>
                <SelectItem value="namus_unidentified">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500" />
                    NamUs Unidentified{facetCount('by_source','namus_unidentified') ? ` (${fmtCount(facetCount('by_source','namus_unidentified'))})` : ''}
                  </span>
                </SelectItem>
                <SelectItem value="charley">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    Charley Project{facetCount('by_source','charley') ? ` (${fmtCount(facetCount('by_source','charley'))})` : ''}
                  </span>
                </SelectItem>
                <SelectItem value="doenetwork">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-cyan-500" />
                    Doe Network{facetCount('by_source','doenetwork') ? ` (${fmtCount(facetCount('by_source','doenetwork'))})` : ''}
                  </span>
                </SelectItem>
                <SelectItem value="namus_unclaimed">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-rose-500" />
                    NamUs Unclaimed{facetCount('by_source','namus_unclaimed') ? ` (${fmtCount(facetCount('by_source','namus_unclaimed'))})` : ''}
                  </span>
                </SelectItem>
                <SelectItem value="community">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    Community Reports
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* State Section */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Map className="w-3.5 h-3.5" />
              State
            </h3>
            <Select value={stateFilter} onValueChange={handleStateChange}>
              <SelectTrigger className="rounded-full">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map(state => {
                  const c = facetCount('by_state', state);
                  return (
                    <SelectItem key={state} value={state}>
                      {state}{c ? ` (${fmtCount(c)})` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Urgent Toggle */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" />
              Priority
            </h3>
            <Button
              variant={urgentOnly ? 'default' : 'outline'}
              size="sm"
              onClick={toggleUrgent}
              className={`rounded-full w-full justify-start ${urgentOnly ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Urgent Cases Only
            </Button>
          </div>

          {/* Advanced Filters Section */}
          <div className="border-t pt-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" />
              Advanced Filters
            </h3>
            
            <div className="space-y-3">
              {/* Gender */}
              <Select value={genderFilter} onValueChange={setGenderFilter}>
                <SelectTrigger className="rounded-full text-sm">
                  <SelectValue placeholder="Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  <SelectItem value="male">Male{facetCount('by_gender','male') ? ` (${fmtCount(facetCount('by_gender','male'))})` : ''}</SelectItem>
                  <SelectItem value="female">Female{facetCount('by_gender','female') ? ` (${fmtCount(facetCount('by_gender','female'))})` : ''}</SelectItem>
                </SelectContent>
              </Select>

              {/* Age */}
              <Select value={ageFilter} onValueChange={setAgeFilter}>
                <SelectTrigger className="rounded-full text-sm">
                  <SelectValue placeholder="Age Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  <SelectItem value="child">Child (0-12)</SelectItem>
                  <SelectItem value="teen">Teen (13-17)</SelectItem>
                  <SelectItem value="young_adult">Young Adult (18-25)</SelectItem>
                  <SelectItem value="adult">Adult (26-64)</SelectItem>
                  <SelectItem value="senior">Senior (65+)</SelectItem>
                </SelectContent>
              </Select>

              {/* Race */}
              <Select value={raceFilter} onValueChange={setRaceFilter}>
                <SelectTrigger className="rounded-full text-sm">
                  <SelectValue placeholder="Race/Ethnicity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Races</SelectItem>
                  <SelectItem value="white">White{facetCount('by_race','white') ? ` (${fmtCount(facetCount('by_race','white'))})` : ''}</SelectItem>
                  <SelectItem value="black">Black{facetCount('by_race','black') ? ` (${fmtCount(facetCount('by_race','black'))})` : ''}</SelectItem>
                  <SelectItem value="hispanic">Hispanic/Latino{facetCount('by_race','hispanic') ? ` (${fmtCount(facetCount('by_race','hispanic'))})` : ''}</SelectItem>
                  <SelectItem value="asian">Asian{facetCount('by_race','asian') ? ` (${fmtCount(facetCount('by_race','asian'))})` : ''}</SelectItem>
                  <SelectItem value="native">Native American{facetCount('by_race','native') ? ` (${fmtCount(facetCount('by_race','native'))})` : ''}</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>

              {/* Hair Color */}
              <Select value={hairColorFilter} onValueChange={setHairColorFilter}>
                <SelectTrigger className="rounded-full text-sm">
                  <SelectValue placeholder="Hair Color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hair Colors</SelectItem>
                  <SelectItem value="black">Black{facetCount('by_hair_color','black') ? ` (${fmtCount(facetCount('by_hair_color','black'))})` : ''}</SelectItem>
                  <SelectItem value="brown">Brown{facetCount('by_hair_color','brown') ? ` (${fmtCount(facetCount('by_hair_color','brown'))})` : ''}</SelectItem>
                  <SelectItem value="blonde">Blonde{facetCount('by_hair_color','blonde') ? ` (${fmtCount(facetCount('by_hair_color','blonde'))})` : ''}</SelectItem>
                  <SelectItem value="red">Red{facetCount('by_hair_color','red') ? ` (${fmtCount(facetCount('by_hair_color','red'))})` : ''}</SelectItem>
                  <SelectItem value="gray">Gray/White{facetCount('by_hair_color','gray') ? ` (${fmtCount(facetCount('by_hair_color','gray'))})` : ''}</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>

              {/* Eye Color */}
              <Select value={eyeColorFilter} onValueChange={setEyeColorFilter}>
                <SelectTrigger className="rounded-full text-sm">
                  <SelectValue placeholder="Eye Color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Eye Colors</SelectItem>
                  <SelectItem value="brown">Brown{facetCount('by_eye_color','brown') ? ` (${fmtCount(facetCount('by_eye_color','brown'))})` : ''}</SelectItem>
                  <SelectItem value="blue">Blue{facetCount('by_eye_color','blue') ? ` (${fmtCount(facetCount('by_eye_color','blue'))})` : ''}</SelectItem>
                  <SelectItem value="green">Green{facetCount('by_eye_color','green') ? ` (${fmtCount(facetCount('by_eye_color','green'))})` : ''}</SelectItem>
                  <SelectItem value="hazel">Hazel{facetCount('by_eye_color','hazel') ? ` (${fmtCount(facetCount('by_eye_color','hazel'))})` : ''}</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>

              {/* Has Photo */}
              <Select value={hasPhotoFilter} onValueChange={setHasPhotoFilter}>
                <SelectTrigger className="rounded-full text-sm">
                  <SelectValue placeholder="Photo Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cases</SelectItem>
                  <SelectItem value="yes">Has Photo{facetCount('by_photo','yes') ? ` (${fmtCount(facetCount('by_photo','yes'))})` : ''}</SelectItem>
                  <SelectItem value="no">No Photo{facetCount('by_photo','no') ? ` (${fmtCount(facetCount('by_photo','no'))})` : ''}</SelectItem>
                </SelectContent>
              </Select>

              {/* Year Missing */}
              <Select value={yearMissingFilter} onValueChange={setYearMissingFilter}>
                <SelectTrigger className="rounded-full text-sm">
                  <SelectValue placeholder="Year Missing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2022">2022</SelectItem>
                  <SelectItem value="2020s">2020-2024</SelectItem>
                  <SelectItem value="2010s">2010-2019</SelectItem>
                  <SelectItem value="2000s">2000-2009</SelectItem>
                  <SelectItem value="older">Before 2000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Apply Button */}
        <div className="p-4 border-t bg-background">
          <Button 
            onClick={() => setFilterDrawerOpen(false)}
            className="w-full rounded-full bg-trust-blue-500 hover:bg-trust-blue-600"
          >
            Show {filteredCases.length} Results
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
    
    {/* Mobile Sticky Bottom Action Bar */}
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 safe-area-bottom">
      <div className="grid grid-cols-4 gap-1 px-2 py-2">
        {/* Search */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex flex-col items-center justify-center py-2 px-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
        >
          <Search className="w-5 h-5 text-trust-blue-500 mb-1" />
          <span className="text-[10px] text-muted-foreground font-medium">Search</span>
        </button>
        
        {/* Map */}
        <Link 
          to="/map"
          className="flex flex-col items-center justify-center py-2 px-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
        >
          <Map className="w-5 h-5 text-hope-green-500 mb-1" />
          <span className="text-[10px] text-muted-foreground font-medium">Map</span>
        </Link>
        
        {/* Share */}
        <button
          onClick={async () => {
            const shareUrl = window.location.href;
            const shareText = "🔍 Help find missing persons. Browse cases and share to increase visibility.";
            if (navigator.share) {
              try {
                await navigator.share({ title: 'LinkTheMissing - Explore Cases', text: shareText, url: shareUrl });
              } catch (err) {
                if (err.name !== 'AbortError') {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success('Link copied!');
                }
              }
            } else {
              navigator.clipboard.writeText(shareUrl);
              toast.success('Link copied to clipboard!');
            }
          }}
          className="flex flex-col items-center justify-center py-2 px-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
        >
          <Share2 className="w-5 h-5 text-purple-500 mb-1" />
          <span className="text-[10px] text-muted-foreground font-medium">Share</span>
        </button>
        
        {/* Submit Tip */}
        <Link 
          to="/tips"
          className="flex flex-col items-center justify-center py-2 px-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
        >
          <MessageCircleWarning className="w-5 h-5 text-safety-orange-500 mb-1" />
          <span className="text-[10px] text-muted-foreground font-medium">Tip</span>
        </Link>
      </div>
    </div>
    
    {/* Bottom padding to account for sticky bar */}
    <div className="md:hidden h-20" />
    </>
  );
}
